import {
  isValidEmailAddress,
  isValidMobileNumber,
} from "../../utilities/validators";
import {
  setEmailAddress,
  setFirstName,
  setLastName,
  setTermsAndConditions,
  setTelephone,
  setPrivacyPolicy,
} from "../../state/entry-form-details/reducer";
import { useEffect, useState } from "react";
import { Button } from "@asosteam/asos-web-component-library-button";
import { Checkbox } from "@asosteam/asos-web-component-library-checkbox";
import { ErrorMessage } from "../../components/error-message";
import PropTypes from "prop-types";
import ReCaptcha from "react-google-recaptcha";
import { TextField } from "../../components/text-field";
import { createCompetitionEntry } from "../../utilities/create-competition-entry";
import styles from "./styles";
import { useEntryFormDetails } from "../../context/entry-details";
import { isCompetitionOpen } from "../../utilities/competition-status";

const renderErrorUI = (formStatus) => {
  function renderErrorResponse(errorMessage) {
    return (
      <div className={styles.errorResponse}>
        <h3 className="app_london2">HMM, SOMETHING&apos;S NOT RIGHT!</h3>
        <p className="app_leavesden2">{errorMessage}</p>
      </div>
    );
  }

  if (formStatus === "error") {
    return renderErrorResponse(
      "Your submission didn't go through :( Please try again"
    );
  }

  if (formStatus === "duplicateEmail") {
    return renderErrorResponse(
      "An entry with this email address already exists"
    );
  }
};

const setResponseError = (e, setFormStatus) => {
  if (e.response.status === 409) {
    setFormStatus("duplicateEmail");
  } else {
    setFormStatus("error");
  }
};

export default function EntryForm({
  appConfig,
  ignoreCaptcha,
  anchorRef,
  setFormStatus,
  formStatus,
  scrollToTop,
}) {
  const { recaptchaSiteKey } = appConfig;
  const [submitInProgress, setSubmitInProgress] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(ignoreCaptcha);

  const [state, dispatch] = useEntryFormDetails();
  const {
    firstName,
    lastName,
    emailAddress,
    telephone,
    termsAndConditions,
    privacyPolicy,
  } = state.entryFormDetails;

  const [validationErrors, setValidationErrors] = useState({
    firstName: false,
    lastName: false,
    telephone: false,
    emailAddress: false,
    termsAndConditions: false,
    privacyPolicy: false,
  });

  useEffect(() => {
    setHasErrors(Object.values(validationErrors).find((item) => item));
  }, [validationErrors]);

  const handleSubmit = async () => {
    if (isCompetitionOpen()) {
      const validationErrorState = {
        firstName: !firstName,
        lastName: !lastName,
        emailAddress: !isValidEmailAddress(emailAddress),
        termsAndConditions: !termsAndConditions,
        telephone: !isValidMobileNumber(telephone),
        isCaptchaVerified: !isCaptchaVerified,
        privacyPolicy: !privacyPolicy,
      };

      setValidationErrors(validationErrorState);
      const errorsPresent = Object.values(validationErrorState).find(
        (item) => item
      );
      setHasErrors(errorsPresent);
      if (!errorsPresent) {
        const payload = new FormData();
        Object.keys(state.entryFormDetails).forEach((key) => {
          if (key !== "privacyPolicy") {
            payload.append(key, state.entryFormDetails[key]);
          }
        });

        try {
          const response = await createCompetitionEntry(
            "/api/customer/competitions/v1/mastercard",
            payload
          );

          if (response.status === 201) {
            setFormStatus("success");
          } else {
            setFormStatus("error");
          }
        } catch (e) {
          setResponseError(e, setFormStatus);
        }
      }
    } else {
      scrollToTop();
      setFormStatus("closed");
    }
  };

  const onSubmit = async () => {
    setSubmitInProgress(true);
    await handleSubmit();
    setSubmitInProgress(false);
  };

  return (
    <div className={styles.container} ref={anchorRef}>
      <div className={styles.form}>
        {renderErrorUI(formStatus)}
        <div className={styles.formControl}>
          <label htmlFor="firstName" className="app_london3">
            First Name:
          </label>
          <TextField
            id="firstName"
            ariaLabel="First Name"
            initialValue={firstName}
            onChange={(e) => {
              dispatch(setFirstName(e.target.value));
              setValidationErrors({
                ...validationErrors,
                firstName: !e.target.value,
              });
            }}
            hasError={validationErrors.firstName}
            errorMessage="Hold up! You need to enter your first name"
            required
          />
        </div>
        <div className={styles.formControl}>
          <label htmlFor="firstName" className="app_london3">
            Last Name:
          </label>
          <TextField
            id="lastName"
            ariaLabel="Last Name"
            initialValue={lastName}
            onChange={(e) => {
              dispatch(setLastName(e.target.value));
              setValidationErrors({
                ...validationErrors,
                lastName: !e.target.value,
              });
            }}
            hasError={validationErrors.lastName}
            errorMessage="Hold up! You need to enter your last name"
            required
          />
        </div>
        <div className={styles.formControl}>
          <label htmlFor="telephone" className="app_london3">
            Telephone Number:
          </label>
          <TextField
            id="telephone"
            ariaLabel="Telephone Number"
            initialValue={telephone}
            onChange={(e) => {
              dispatch(setTelephone(e.target.value));
              setValidationErrors({
                ...validationErrors,
                telephone: !isValidMobileNumber(e.target.value),
              });
            }}
            hasError={validationErrors.telephone}
            errorMessage="Hold up! You need to enter a valid telephone number"
            required
          />
        </div>
        <div className={styles.formControl}>
          <label htmlFor="emailAddress" className="app_london3">
            Email Address:
          </label>
          <TextField
            id="emailAddress"
            ariaLabel="Email Address"
            initialValue={emailAddress}
            onChange={(e) => {
              dispatch(setEmailAddress(e.target.value));
              setValidationErrors({
                ...validationErrors,
                emailAddress: !isValidEmailAddress(e.target.value),
              });
            }}
            hasError={validationErrors.emailAddress}
            errorMessage="Hold up! You need to enter a valid email address"
            required
          />
        </div>
        <div className={styles.formControl}>
          <div className={styles.tsAndCs}>
            <Checkbox
              ariaDescribedBy="Terms and Conditions"
              ariaLabel="Terms and Conditions"
              data-auto-id="Terms and Conditions"
              id="termsAndConditions"
              input={{
                name: "termsAndConditions",
                onChange: (e) => {
                  dispatch(setTermsAndConditions(e.target.checked ? 1 : 0));
                  setValidationErrors({
                    ...validationErrors,
                    termsAndConditions: !e.target.checked,
                  });
                },
                value: termsAndConditions,
              }}
            />
            <label className="app_leavesden2" htmlFor="termsAndConditions">
              I accept the{" "}
              <a href="https://www.asos.com/discover/marketing-terms-and-conditions/190110-mastercard-terms/">
                Terms & Conditions
              </a>{" "}
              and confirm that I am over the age of 18.
            </label>
          </div>
          {validationErrors.termsAndConditions && (
            <ErrorMessage message="Hold up! You need to accept the Ts&Cs" />
          )}
        </div>
        <div className={styles.formControl}>
          <div className={styles.tsAndCs}>
            <Checkbox
              ariaDescribedBy="Privacy Policy"
              ariaLabel="Privacy Policy"
              data-auto-id="Privacy Policy"
              id="privacyPolicy"
              input={{
                name: "privacyPolicy",
                onChange: (e) => {
                  dispatch(setPrivacyPolicy(e.target.checked ? 1 : 0));
                  setValidationErrors({
                    ...validationErrors,
                    privacyPolicy: !e.target.checked,
                  });
                },
                value: privacyPolicy,
              }}
            />
            <label className="app_leavesden2" htmlFor="privacyPolicy">
              I consent to my data being processed for the purposes stated above
              and in accordance with the{" "}
              <a href="https://www.asos.com/privacy-policy/">privacy notice</a>
              {"."}
            </label>
          </div>
          {validationErrors.privacyPolicy && (
            <ErrorMessage message="Hold up! You need to accept the Privacy Policy" />
          )}
        </div>

        <ReCaptcha
          className={styles.captcha}
          sitekey={recaptchaSiteKey}
          onChange={() => {
            setIsCaptchaVerified(true);
            setValidationErrors({
              ...validationErrors,
              isCaptchaVerified: false,
            });
          }}
          onExpired={() => setIsCaptchaVerified(false)}
        />
        <Button
          className="app_button"
          onClick={onSubmit}
          disabled={!isCaptchaVerified || submitInProgress || hasErrors}
          isInProgress={submitInProgress}
        >
          Enter Now
        </Button>
      </div>
    </div>
  );
}

EntryForm.propTypes = {
  appConfig: PropTypes.shape({
    recaptchaSiteKey: PropTypes.string.isRequired,
  }),
  ignoreCaptcha: PropTypes.bool,
  anchorRef: PropTypes.object,
  setFormStatus: PropTypes.func.isRequired,
  scrollToTop: PropTypes.func.isRequired,
  formStatus: PropTypes.string,
};

EntryForm.defaultProps = {
  ignoreCaptcha: false,
  anchorRef: null,
  formStatus: "",
};
